import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  FacebookShareButton, FacebookIcon,
  WhatsappShareButton, WhatsappIcon,
  TwitterShareButton,
  EmailShareButton, EmailIcon,
} from "react-share";
import http from "../../../http-common";
import XTwitterIcon from "../../common/XTwitterIcon";
import ShareQrCode from "./ShareQrCode";

const XIcon = () => (
  <div className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-black">
    <XTwitterIcon fill="white" width={18} height={18} />
  </div>
);

export default function WaitlistStatusModal({ fepkId, storageKey, sessionToken, onClose, onLeave }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null); // { entry, position, tierProgress }
  const [summary, setSummary] = useState({ totalCount: 0, optedInNames: [] });
  const [showConfirmLeave, setShowConfirmLeave] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [savingName, setSavingName] = useState(false);

  // A logged-in member who joined on another device/browser (or cleared storage)
  // has no local waitlist token, but the app already resolved their membership via
  // their session - fall back to that instead of a bare "Authorization token missing".
  const token = localStorage.getItem(storageKey) || sessionToken;

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [statusRes, summaryRes] = await Promise.all([
        http.get(`/waitlist/${fepkId}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        http.get(`/waitlist/${fepkId}/summary`),
      ]);

      setData(statusRes.data);
      setSummary(summaryRes.data || { totalCount: 0, optedInNames: [] });
    } catch (err) {
      setError(err.response?.data?.message || t("Failed to load waitlist status."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fepkId && token) {
      loadData();
    } else {
      setLoading(false);
      setError(t("Authorization token missing."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fepkId, token]);

  const handleCopyLink = () => {
    if (!data?.entry?.referralCode) return;
    const shareUrl = `${window.location.origin}/epk/${fepkId}?ref=${data.entry.referralCode}`;
    
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        toast.success(t("Referral link copied to clipboard!"));
      })
      .catch(() => {
        toast.error(t("Failed to copy link."));
      });
  };

  const handleToggleShowName = async () => {
    const next = !data?.entry?.showNamePublicly;
    setSavingName(true);
    try {
      await http.patch(
        "/waitlist/me",
        { showNamePublicly: next, fepkId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData((prev) => ({ ...prev, entry: { ...prev.entry, showNamePublicly: next } }));
      toast.success(t("Saved."));
      // Refresh Recent joins; the save already succeeded, so a failure here stays silent
      http.get(`/waitlist/${fepkId}/summary`)
        .then((summaryRes) => setSummary(summaryRes.data || { totalCount: 0, optedInNames: [] }))
        .catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.message || t("Failed to save. Please try again."));
    } finally {
      setSavingName(false);
    }
  };

  const handleLeaveWaitlist = async () => {
    setLeaving(true);
    try {
      await http.delete("/waitlist/me", {
        headers: { Authorization: `Bearer ${token}` },
        data: { fepkId } // include in body just in case
      });
      localStorage.removeItem(storageKey);
      toast.success(t("Successfully left the waitlist"));
      onLeave();
    } catch (err) {
      toast.error(err.response?.data?.message || t("Failed to leave waitlist."));
    } finally {
      setLeaving(false);
      setShowConfirmLeave(false);
    }
  };

  const entry = data?.entry;
  const position = data?.position;
  const tierProgress = data?.tierProgress || [];
  const referralUrl = entry?.referralCode
    ? `${window.location.origin}/epk/${fepkId}?ref=${entry.referralCode}`
    : "";
  const referralShareTitle = t("Join me on the waitlist and let's both climb the list!");

  // Find next tier
  const nextTier = tierProgress.find((t) => !t.earned);
  const progressPercentage = nextTier ? Math.round(nextTier.progress * 100) : 100;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/60 tw-p-4">
      <div className="tw-w-full tw-max-w-md tw-rounded-2xl tw-bg-[#280D41] tw-border tw-border-[#712CB0]/30 tw-p-6 tw-shadow-2xl tw-max-h-[90vh] tw-overflow-y-auto">
        
        {/* Header */}
        <div className="tw-mb-5 tw-flex tw-items-center tw-justify-between">
          <h2 className="tw-m-0 tw-text-xl tw-font-semibold tw-text-white">
            {t("Waitlist Status")}
          </h2>
          <button
            onClick={onClose}
            className="tw-text-white/50 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-lg tw-leading-none"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="tw-py-8 tw-text-center">
            <div className="tw-mx-auto tw-h-8 tw-w-8 tw-animate-spin tw-rounded-full tw-border-4 tw-border-[#E81A84] tw-border-t-transparent tw-mb-2" />
            <p className="tw-text-sm tw-text-white/50">{t("Loading status...")}</p>
          </div>
        ) : error ? (
          <div className="tw-py-4 tw-text-center">
            <p className="tw-text-sm tw-text-red-400 tw-mb-4">{error}</p>
            <button
              onClick={onClose}
              className="tw-rounded-lg tw-bg-white/10 tw-px-4 tw-py-2 tw-text-sm tw-text-white hover:tw-bg-white/20 tw-border-none tw-cursor-pointer"
            >
              {t("Close")}
            </button>
          </div>
        ) : (
          <div className="tw-space-y-6">
            
            {/* Stats Row */}
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
              <div className="tw-rounded-xl tw-bg-[#1C0039]/50 tw-p-4 tw-text-center tw-border tw-border-[#712CB0]/10">
                <span className="tw-block tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-white/40 tw-mb-1">
                  {t("Your Position")}
                </span>
                <span className="tw-text-3xl tw-font-bold tw-text-[#FF43A7]">
                  #{position || "-"}
                </span>
              </div>
              <div className="tw-rounded-xl tw-bg-[#1C0039]/50 tw-p-4 tw-text-center tw-border tw-border-[#712CB0]/10">
                <span className="tw-block tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-white/40 tw-mb-1">
                  {t("Referrals")}
                </span>
                <span className="tw-text-3xl tw-font-bold tw-text-[#FF43A7]">
                  {entry?.referralCount || 0}
                </span>
              </div>
            </div>

            {/* Progress to Next Reward */}
            {nextTier ? (
              <div className="tw-rounded-xl tw-bg-[#1C0039]/30 tw-p-4 tw-border tw-border-[#712CB0]/10">
                <div className="tw-mb-2 tw-flex tw-justify-between tw-text-xs">
                  <span className="tw-font-medium tw-text-white/70">
                    {t("Progress to next reward")}
                  </span>
                  <span className="tw-font-semibold tw-text-[#FF43A7]">
                    {progressPercentage}%
                  </span>
                </div>
                <div className="tw-h-2.5 tw-w-full tw-rounded-full tw-bg-white/10 tw-overflow-hidden">
                  <div
                    className="tw-h-full tw-rounded-full tw-bg-gradient-to-r tw-from-[#FF43A7] tw-to-[#E81A84] tw-transition-all tw-duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <div className="tw-mt-3">
                  <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-white">
                    {nextTier.rewardLabel}
                  </p>
                  <p className="tw-m-0 tw-text-xs tw-text-white/50 tw-mt-0.5">
                    {nextTier.rewardDetail}
                  </p>
                  <span className="tw-inline-block tw-mt-2 tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded tw-bg-[#712CB0]/20 tw-text-white/70">
                    {nextTier.basis === "referrals"
                      ? t("Requires {{count}} referrals (need {{needed}} more)", {
                          count: nextTier.threshold,
                          needed: Math.max(0, nextTier.threshold - (entry?.referralCount || 0))
                        })
                      : t("Requires top {{pos}} position", { pos: nextTier.threshold })}
                  </span>
                </div>
              </div>
            ) : (
              <div className="tw-rounded-xl tw-bg-[#1C0039]/30 tw-p-4 tw-text-center tw-border tw-border-[#712CB0]/10">
                <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-white">
                  🎉 {t("All rewards unlocked!")}
                </p>
                <p className="tw-m-0 tw-text-xs tw-text-white/50 tw-mt-1">
                  {t("You have achieved the highest tier.")}
                </p>
              </div>
            )}

            {/* Reward Tiers List */}
            {tierProgress.length > 0 && (
              <div>
                <h4 className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-white/40 tw-mb-3">
                  {t("Reward Tiers")}
                </h4>
                <div className="tw-space-y-2">
                  {tierProgress.map((tier, idx) => (
                    <div
                      key={idx}
                      className={`tw-flex tw-items-center tw-justify-between tw-p-3 tw-rounded-lg tw-border ${
                        tier.earned
                          ? "tw-bg-[#FF43A7]/5 tw-border-[#FF43A7]/20"
                          : "tw-bg-white/5 tw-border-white/5"
                      }`}
                    >
                      <div className="tw-min-w-0 tw-flex-1 tw-pr-3">
                        <p className={`tw-m-0 tw-text-sm tw-font-medium ${tier.earned ? "tw-text-white" : "tw-text-white/60"}`}>
                          {tier.rewardLabel}
                        </p>
                        <p className="tw-m-0 tw-text-xs tw-text-white/40 tw-truncate">
                          {tier.rewardDetail}
                        </p>
                      </div>
                      <div className="tw-flex-shrink-0 tw-flex tw-items-center tw-gap-2">
                        <span className="tw-text-[10px] tw-text-white/50">
                          {tier.basis === "referrals"
                            ? `${tier.threshold} ref`
                            : `Top ${tier.threshold}`}
                        </span>
                        {tier.earned ? (
                          <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-500/20 tw-text-green-500 tw-text-xs">
                            ✓
                          </span>
                        ) : (
                          <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/10 tw-text-white/30 tw-text-xs">
                            🔒
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Share Link Section */}
            <div className="tw-rounded-xl tw-bg-[#1C0039]/30 tw-p-4 tw-border tw-border-[#712CB0]/10">
              <span className="tw-block tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-white/40 tw-mb-2">
                {t("Share with friends to climb the list")}
              </span>

              {referralUrl && (
                <div className="tw-flex tw-justify-between tw-mb-3">
                  <FacebookShareButton url={referralUrl} quote={referralShareTitle} className="hover:tw-scale-110 tw-transition-transform">
                    <FacebookIcon size={36} round />
                  </FacebookShareButton>
                  <WhatsappShareButton url={referralUrl} title={referralShareTitle} className="hover:tw-scale-110 tw-transition-transform">
                    <WhatsappIcon size={36} round />
                  </WhatsappShareButton>
                  <TwitterShareButton url={referralUrl} title={referralShareTitle} className="hover:tw-scale-110 tw-transition-transform">
                    <XIcon />
                  </TwitterShareButton>
                  <EmailShareButton url={referralUrl} subject={referralShareTitle} body={`${referralShareTitle}\n${referralUrl}`} className="hover:tw-scale-110 tw-transition-transform">
                    <EmailIcon size={36} round />
                  </EmailShareButton>
                </div>
              )}

              <div className="tw-flex tw-gap-2">
                <input
                  type="text"
                  readOnly
                  value={referralUrl}
                  className="tw-flex-1 tw-bg-[#1C0039] tw-border tw-border-[#712CB0]/30 tw-rounded-lg tw-px-3 tw-py-2 tw-text-xs tw-text-white/70 focus:tw-outline-none"
                />
                <button
                  onClick={handleCopyLink}
                  className="tw-rounded-lg tw-bg-[#FF43A7] tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-[#e03290] tw-transition-colors tw-border-none tw-cursor-pointer"
                >
                  {t("Copy")}
                </button>
              </div>

              {referralUrl && (
                <div className="tw-mt-3 tw-flex tw-items-center tw-gap-3">
                  <ShareQrCode value={referralUrl} compact size={80} />
                  <p className="tw-m-0 tw-text-[11px] tw-leading-relaxed tw-text-white/50">
                    {t("Share in person: let them scan it, the referral is tagged to you.")}
                  </p>
                </div>
              )}
            </div>

            {/* Public name setting */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-bg-[#1C0039]/30 tw-p-4 tw-border tw-border-[#712CB0]/10">
              <div className="tw-min-w-0">
                <p id="waitlist-show-name-label" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-white">
                  {t("Show my first name publicly")}
                </p>
                <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-white/50">
                  {entry?.showNamePublicly
                    ? t("Visible in Recent joins on this film page.")
                    : t("Hidden. You count toward the total only.")}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!entry?.showNamePublicly}
                aria-labelledby="waitlist-show-name-label"
                onClick={handleToggleShowName}
                disabled={savingName}
                className={`tw-relative tw-h-6 tw-w-11 tw-flex-shrink-0 tw-rounded-full tw-border-none tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 ${
                  entry?.showNamePublicly ? "tw-bg-[#FF43A7]" : "tw-bg-white/25"
                }`}
              >
                <span
                  className={`tw-absolute tw-left-0.5 tw-top-0.5 tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-shadow tw-transition-transform ${
                    entry?.showNamePublicly ? "tw-translate-x-5" : "tw-translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Social Proof Summary */}
            <div className="tw-border-t tw-border-white/10 tw-pt-4">
              <div className="tw-text-center tw-text-xs tw-text-white/60">
                <span className="tw-font-bold tw-text-white">{summary.totalCount}</span>{" "}
                {t("people have already joined this waitlist!")}
              </div>
              {summary.optedInNames?.length > 0 && (
                <div className="tw-mt-2 tw-flex tw-flex-wrap tw-justify-center tw-gap-1 tw-text-[10px] tw-text-white/40">
                  <span className="tw-font-medium">{t("Recent joins:")}</span>
                  {summary.optedInNames.slice(0, 5).map((name, i) => (
                    <span key={i} className="tw-bg-white/5 tw-px-1.5 tw-py-0.5 tw-rounded">
                      {name}
                    </span>
                  ))}
                  {summary.optedInNames.length > 5 && <span>...</span>}
                </div>
              )}
            </div>

            {/* Footer / Leave Button */}
            <div className="tw-border-t tw-border-white/10 tw-pt-4 tw-flex tw-justify-between tw-items-center">
              {!showConfirmLeave ? (
                <button
                  onClick={() => setShowConfirmLeave(true)}
                  className="tw-bg-transparent tw-border-none tw-text-xs tw-text-red-400 hover:tw-underline tw-cursor-pointer tw-p-0"
                >
                  {t("Leave Waitlist")}
                </button>
              ) : (
                <div className="tw-flex tw-flex-col tw-items-start tw-w-full">
                  <p className="tw-m-0 tw-text-xs tw-text-red-400 tw-font-medium tw-mb-2">
                    {t("Are you sure? This will delete your position and referrals permanently.")}
                  </p>
                  <div className="tw-flex tw-gap-2">
                    <button
                      onClick={handleLeaveWaitlist}
                      disabled={leaving}
                      className="tw-rounded tw-bg-red-500 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-red-600 tw-border-none tw-cursor-pointer disabled:tw-opacity-50"
                    >
                      {leaving ? t("Leaving...") : t("Yes, Leave")}
                    </button>
                    <button
                      onClick={() => setShowConfirmLeave(false)}
                      className="tw-rounded tw-bg-white/10 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-white/20 tw-border-none tw-cursor-pointer"
                    >
                      {t("Cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
